import React, { useState } from 'react';
import styled, { css } from 'styled-components';
import { useFormContext } from 'react-hook-form';
import { colors } from '../../styles/colors';
import { TextSmall } from '../Typography';
import { EyeIcon, EyeOffIcon } from './icons';

export type FieldState = 'default' | 'hover' | 'focus' | 'disabled' | 'error' | 'successful';

interface FieldProps extends Omit<React.InputHTMLAttributes<HTMLInputElement | HTMLTextAreaElement>, 'type'> {
  name: string;
  label?: string;
  placeholder?: string;
  type?: 'text' | 'password' | 'email' | 'textarea';
  className?: string;
  disabled?: boolean;
  startIcon?: React.ReactNode;
  endIcon?: React.ReactNode;
  error?: string;
  isSuccessful?: boolean;
  value?: string;
  height?: string;
  onChange?: (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => void;
  onBlur?: (e: React.FocusEvent<HTMLInputElement | HTMLTextAreaElement>) => void;
}

interface StyledFieldProps {
  $state: FieldState;
  $height?: string;
  $hasStartIcon?: boolean;
}

const StyledFieldWrapper = styled.div`
    position: relative;
    width: 100%;
    display: flex;
    flex-direction: column;
    gap: 4px;
`;

const StyledTextArea = styled.textarea<StyledFieldProps>`
    width: 100%;
    min-height: ${({$height}) => $height || '100px'};
    padding: 16px;
    border-radius: 6px;
    border: 1px solid;
    outline: none;
    font-size: 14px;
    transition: all 0.2s ease-in-out;
    resize: none;
    font-family: inherit;

    ${({$state}) => {
        switch ($state) {
            case 'hover':
                return css`
                    border-color: ${colors.hoverState};
                    background-color: ${colors.accentQuaternary};
                `;
            case 'focus':
                return css`
                    background-color: ${colors.accentQuaternary};
                    border-color: ${colors.accentPrimary};
                    color: ${colors.accentPrimary};
                `;
            case 'disabled':
                return css`
                    background-color: ${colors.accentQuaternary};
                    border-color: ${colors.accentQuaternary3};
                    color: ${colors.accentQuaternary4};
                    cursor: not-allowed;
                `;
            case 'error':
                return css`
                    background-color: ${colors.errorLight};
                    border-color: ${colors.errorState};
                    color: ${colors.dark};
                `;
            case 'successful':
                return css`
                    background-color: ${colors.accentWhite};
                    border-color: ${colors.accentTertiary};
                `;
            default: 
                return css`
                    background-color: ${colors.accentWhite};
                    border-color: ${colors.accentQuaternary3};
                    color:  ${colors.accentQuaternary5};
                `;
        }
    }}
`;

const StyledInput = styled.input<StyledFieldProps>`
    width: 100%;
    min-height: ${({$height}) => $height || '36px'};
    padding: ${props => props.$hasStartIcon ? '0 16px 0 40px' : '0 16px'};
    border-radius: 6px;
    border: 1px solid;
    outline: none;
    font-size: 14px;
    transition: all 0.2s ease-in-out;

    ${({$state}) => {
        switch ($state) {
            case 'hover':
                return css`
                    border-color: ${colors.hoverState};
                    background-color: ${colors.accentQuaternary};
                `;
            case 'focus':
                return css`
                    background-color: ${colors.accentQuaternary};
                    border-color: ${colors.accentPrimary};
                    color: ${colors.accentPrimary};
                `;
            case 'disabled':
                return css`
                    background-color: ${colors.accentQuaternary};
                    border-color: ${colors.accentQuaternary3};
                    color: ${colors.accentQuaternary4};
                    cursor: not-allowed;
                `;
            case 'error':
                return css`
                    background-color: ${colors.errorLight};
                    border-color: ${colors.errorState};
                    color: ${colors.dark};
                `;
            case 'successful':
                return css`
                    background-color: ${colors.accentWhite};
                    border-color: ${colors.accentTertiary};
                `;
            default: 
                return css`
                    background-color: ${colors.accentWhite};
                    border-color: ${colors.accentQuaternary3};
                    color:  ${colors.accentQuaternary5};
                `;
        }
    }}
`;

const IconWrapper = styled.div<{ $state: FieldState; $position?: 'start' | 'end' }>`
    position: absolute;
    ${props => props.$position === 'start' ? 'left: 12px;' : 'right: 12px;'}
    top: 50%;
    transform: translateY(-50%);
    display: flex;
    align-items: center;
    justify-content: center;
    cursor: pointer;

    svg {
        width: 16px;
        height: 16px;

        path {
            fill: ${({$state}) => {
                switch ($state) {
                    case 'disabled':
                        return '#8A8A8A';
                    case 'error':
                        return '#FF6464';
                    case 'successful':
                        return '#648BEF';
                    case 'hover':
                        return '#7C7B7B';
                    case 'focus':
                        return '#7C7B7B';
                    default:
                        return '#7C7B7B';
                }
            }};
        }
    }
`;

const LabelWrapper = styled.div`
    display: flex;
    align-items: center;
    gap: 6px;
`;

const Label = styled(TextSmall)<{ $state: FieldState }>`
    color: ${({$state}) => {
        switch ($state) {
            case 'disabled':
                return '#9AA0A6';
            case 'error':
                return '#D93025';
            case 'successful':
                return '#34A853';
            default:
                return colors.accentPrimary;
        }
    }}!important;
`;

export const Field: React.FC<FieldProps> = ({
                                              name,
                                              label,
                                              placeholder,
                                              type = 'text',
                                              className,
                                              disabled = false,
                                              startIcon,
                                              endIcon,
                                              error,
                                              isSuccessful = false,
                                              value,
                                              onChange,
                                              height,
                                              onBlur,
                                              ...props
                                            }) => {
  const [isHovered, setIsHovered] = useState(false);
  const [isFocused, setIsFocused] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const formContext = useFormContext();
  const fieldError = error || (formContext?.formState?.errors?.[name]?.message as string);

  const getFieldState = (): FieldState => {
    if (disabled) return 'disabled';
    if (fieldError) return 'error';
    if (isSuccessful) return 'successful';
    if (isFocused) return 'focus';
    if (isHovered) return 'hover';
    return 'default';
  };

  const currentState = getFieldState();

  return (
    <StyledFieldWrapper className={className}>
      {label && (
        <LabelWrapper>
          <Label $state={currentState}>
            {label}
          </Label>
        </LabelWrapper>
      )}
      <div className="relative">
        {type === 'textarea' ? (
          <StyledTextArea
            {...(formContext ? formContext.register(name) : {})}
            name={name}
            placeholder={placeholder}
            disabled={disabled}
            value={value}
            onChange={onChange}
            onBlur={(e) => {
              onBlur?.(e);
              setIsFocused(false);
            }}
            $state={currentState}
            $height={height}
            onFocus={() => setIsFocused(true)}
            onMouseEnter={() => setIsHovered(true)}
            onMouseLeave={() => setIsHovered(false)}
            {...props}
          />
        ) : (
          <StyledInput
            {...(formContext ? formContext.register(name) : {})}
            name={name}
            type={showPassword ? 'text' : type}
            placeholder={placeholder}
            disabled={disabled}
            value={value}
            $height={height}
            onChange={onChange}
            onBlur={(e) => {
              onBlur?.(e);
              setIsFocused(false);
            }}
            $state={currentState}
            $hasStartIcon={!!startIcon}
            onFocus={() => setIsFocused(true)}
            onMouseEnter={() => setIsHovered(true)}
            onMouseLeave={() => setIsHovered(false)}
            {...props}
          />
        )}
        {startIcon && (
          <IconWrapper $state={currentState} $position="start">
            {startIcon}
          </IconWrapper>
        )}
        {type === 'password' ? (
          <IconWrapper
            $state={currentState}
            $position="end"
            onClick={() => !disabled && setShowPassword(!showPassword)}
          >
            {showPassword ? <EyeOffIcon/> : <EyeIcon/>}
          </IconWrapper>
        ) : (
          endIcon && <IconWrapper $state={currentState} $position="end">{endIcon}</IconWrapper>
        )}
      </div>
    </StyledFieldWrapper>
  );
};
